import React, { useState, useEffect } from 'react';
import { Map, Route, MapPin } from 'lucide-react';
import Navigation from '../components/common/Navigation';
import UserLocationMap from '../components/Maps';

const Mappage = () => {
  const [currentRoute, setCurrentRoute] = useState(null);
  const [tracking, setTracking] = useState(false);
  const [routeHistory, setRouteHistory] = useState([
    { id: 1, name: 'Morning Jog', date: '2025-04-03', distance: 3.2, steps: 4350 },
    { id: 2, name: 'Park Loop', date: '2025-04-01', distance: 2.5, steps: 3400 },
    { id: 3, name: 'City Walk', date: '2025-03-28', distance: 4.1, steps: 5600 }
  ]);
  const [stats, setStats] = useState({
    currentSteps: 0,
    currentDistance: 0,
    currentDuration: 0,
    pace: 0
  });
  const [weather, setWeather] = useState({
    temp: 18,
    condition: 'Partly Cloudy',
    humidity: 65,
    wind: 10
  });
  const [showRouteHistory, setShowRouteHistory] = useState(false);

  // Mock tracking functionality
  useEffect(() => {
    let interval;
    if (tracking) {
      interval = setInterval(() => {
        setStats(prev => ({
          ...prev,
          currentSteps: prev.currentSteps + Math.floor(Math.random() * 10),
          currentDistance: +(prev.currentDistance + 0.01).toFixed(2),
          currentDuration: prev.currentDuration + 1,
          pace: +((prev.currentDistance / (prev.currentDuration / 60))).toFixed(2)
        }));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [tracking]);

  const startTracking = () => {
    setTracking(true);
    setStats({
      currentSteps: 0,
      currentDistance: 0,
      currentDuration: 0,
      pace: 0
    });
    setCurrentRoute({
      id: Date.now(),
      name: 'Current Walk',
      coords: []
    });
    setShowRouteHistory(false);
  };

  const stopTracking = () => {
    setTracking(false);
    // In a real app, we would save the route here
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' + secs : secs}`;
  };

  const selectRoute = (routeId) => {
    const selected = routeHistory.find(route => route.id === routeId);
    // In a real app, we would load the route coordinates here
    alert(`Selected route: ${selected.name}`);
  };

  const saveRoute = () => {
    if (stats.currentSteps > 0) {
      const newRoute = {
        id: Date.now(),
        name: 'Walk ' + new Date().toLocaleDateString(),
        date: new Date().toISOString().split('T')[0],
        distance: stats.currentDistance,
        steps: stats.currentSteps
      };
      setRouteHistory([newRoute, ...routeHistory]);
      alert('Route saved successfully!');
      setTracking(false);
    }
  };

  const toggleRouteHistory = () => {
    setShowRouteHistory(!showRouteHistory);
  };

  return (
    <div className="flex h-screen bg-background-light">
      {/* Left sidebar navigation - desktop only */}
      <div className="hidden md:flex flex-col w-64 border-r border-gray-200">
        <Navigation />
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col">
        {/* Top header */}
        <header className="bg-white shadow-sm p-2 md:p-4 flex justify-between items-center">
          <h1 className="text-lg md:text-xl font-semibold">Walking Map</h1>
          <div className="flex items-center space-x-2 md:space-x-4">
            <div className="text-xs md:text-sm text-gray-500">
              <span className="font-medium text-primary">{tracking ? 'Recording' : 'Ready'}</span>
            </div>
            <button 
              onClick={toggleRouteHistory} 
              className="md:hidden bg-gray-100 p-1 rounded-full"
            >
              <Route size={18} />
            </button>
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white">
              <Map size={18} />
            </div>
          </div>
        </header>

        {/* Map and controls */}
        <div className="flex-1 flex flex-col md:flex-row relative">
          {/* The map itself - full screen on mobile */}
          <div className="flex-1 bg-gray-100 relative">
            {/* Map component - full width and height */}
            <div className="absolute inset-0">
              <UserLocationMap />
            </div>

            {/* Floating stats overlay - smaller on mobile */}
            <div className="absolute top-2 left-2 right-2 bg-white rounded-lg shadow-md p-2 md:p-4 text-sm md:text-base">
              <div className="grid grid-cols-4 gap-2">
                <div>
                  <p className="text-xs text-gray-500">Distance</p>
                  <p className="text-base md:text-xl font-bold">{stats.currentDistance.toFixed(2)}<span className="text-xs font-normal">km</span></p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Steps</p>
                  <p className="text-base md:text-xl font-bold">{stats.currentSteps.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Duration</p>
                  <p className="text-base md:text-xl font-bold">{formatDuration(stats.currentDuration)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Pace</p>
                  <p className="text-base md:text-xl font-bold">{stats.pace}<span className="text-xs font-normal">km/h</span></p>
                </div>
              </div>
            </div>

            {/* Weather overlay - smaller on mobile */}
            <div className="absolute bottom-20 md:bottom-4 left-2 bg-white rounded-lg shadow-md p-2">
              <div className="flex items-center">
                <div className="mr-2">
                  <svg className="w-6 h-6 md:w-8 md:h-8 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs md:text-sm font-medium">{weather.condition}</p>
                  <div className="flex text-xs text-gray-500">
                    <p>{weather.temp}°C</p>
                    <p className="mx-1">•</p>
                    <p>H:{weather.humidity}%</p>
                    <p className="mx-1">•</p>
                    <p>W:{weather.wind}km/h</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating tracking controls for mobile */}
            <div className="md:hidden absolute bottom-20 right-2 flex flex-col space-y-2">
              {!tracking ? (
                <button 
                  onClick={startTracking}
                  className="bg-primary text-white p-3 rounded-full shadow-lg"
                >
                  <Route size={24} />
                </button>
              ) : (
                <>
                  <button 
                    onClick={saveRoute}
                    className="bg-secondary text-white p-3 rounded-full shadow-lg"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                      <polyline points="17 21 17 13 7 13 7 21"></polyline>
                      <polyline points="7 3 7 8 15 8"></polyline>
                    </svg>
                  </button>
                  <button 
                    onClick={stopTracking}
                    className="bg-red-500 text-white p-3 rounded-full shadow-lg"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="6" y="6" width="12" height="12" rx="2" ry="2"></rect>
                    </svg>
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Right sidebar with route details and controls - desktop only */}
          <div className="hidden md:block w-80 bg-white border-l border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <h2 className="font-semibold">Route Controls</h2>
            </div>
            
            <div className="p-4 flex space-x-2">
              {!tracking ? (
                <button 
                  onClick={startTracking}
                  className="flex-1 bg-primary text-white py-2 px-4 rounded-md flex items-center justify-center space-x-1"
                >
                  <Route size={18} />
                  <span>Start Route</span>
                </button>
              ) : (
                <>
                  <button 
                    onClick={stopTracking}
                    className="flex-1 bg-red-500 text-white py-2 px-4 rounded-md flex items-center justify-center space-x-1"
                  >
                    <span>Stop</span>
                  </button>
                  <button 
                    onClick={saveRoute}
                    className="flex-1 bg-secondary text-white py-2 px-4 rounded-md flex items-center justify-center space-x-1"
                  >
                    <span>Save</span>
                  </button>
                </>
              )}
            </div>

            <div className="p-4 border-t border-gray-200">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Route History</h3>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {routeHistory.map(route => (
                  <div 
                    key={route.id}
                    onClick={() => selectRoute(route.id)}
                    className="p-3 bg-gray-50 rounded-md cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex justify-between items-center">
                      <h4 className="font-medium">{route.name}</h4>
                      <span className="text-xs text-gray-500">{route.date}</span>
                    </div>
                    <div className="flex text-sm text-gray-500 mt-1">
                      <p>{route.distance} km</p>
                      <p className="mx-1">•</p>
                      <p>{route.steps.toLocaleString()} steps</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Mobile Route History Drawer - slides up from bottom */}
          {showRouteHistory && (
            <div className="md:hidden absolute inset-0 bg-white z-10 flex flex-col">
              <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                <h3 className="font-semibold">Route History</h3>
                <button onClick={toggleRouteHistory} className="p-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <div className="space-y-2">
                  {routeHistory.map(route => (
                    <div 
                      key={route.id}
                      onClick={() => {
                        selectRoute(route.id);
                        setShowRouteHistory(false);
                      }}
                      className="p-3 bg-gray-50 rounded-md cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex justify-between items-center">
                        <h4 className="font-medium">{route.name}</h4>
                        <span className="text-xs text-gray-500">{route.date}</span>
                      </div>
                      <div className="flex text-sm text-gray-500 mt-1">
                        <p>{route.distance} km</p>
                        <p className="mx-1">•</p>
                        <p>{route.steps.toLocaleString()} steps</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Mobile navigation - only visible on small screens */}
        <div className="md:hidden block sticky bottom-0 z-50 bg-white border-t border-gray-200 p-2">
          <Navigation orientation='horizontal' />
        </div>
      </div>
    </div>
  );
};

export default Mappage;